/**
 * Pure geometry for the control-page editor (PMA-BCAST-007): snapping to a
 * grid and to the title-safe area, drag/resize with stage clamping, z-order
 * and widget creation. All coordinates are integer stage pixels.
 */
import type {
  BroadcastAspect,
  BroadcastWidget,
  BroadcastWidgetType,
} from "../../lib/broadcastApi";
import { STAGE_SIZES } from "./stageScale";

export const GRID_PX = 20;
export const SNAP_THRESHOLD_PX = 12;
export const MIN_WIDGET_PX = 40;
export const MAX_WIDGETS = 24;
/** Title-safe inset: 5% of the stage on every side. */
export const TITLE_SAFE_RATIO = 0.05;

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Guides {
  x: number[];
  y: number[];
}

export type ResizeHandle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

export const titleSafeRect = (aspect: BroadcastAspect): Rect => {
  const { w, h } = STAGE_SIZES[aspect];
  const x = Math.round(w * TITLE_SAFE_RATIO);
  const y = Math.round(h * TITLE_SAFE_RATIO);
  return { x, y, w: w - 2 * x, h: h - 2 * y };
};

/** Snap lines per axis: stage edges, title-safe edges, stage centre. */
export const snapLines = (aspect: BroadcastAspect) => {
  const { w, h } = STAGE_SIZES[aspect];
  const s = titleSafeRect(aspect);
  return {
    x: [0, s.x, w / 2, s.x + s.w, w],
    y: [0, s.y, h / 2, s.y + s.h, h],
  };
};

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

/**
 * Best snap of a 1-D span [start, start+size] to `lines`: tries the leading
 * edge, the centre and the trailing edge. Returns the corrected start and
 * the line snapped to (null when nothing is within the threshold).
 */
export function snapSpan(
  start: number,
  size: number,
  lines: number[],
  threshold = SNAP_THRESHOLD_PX,
): { start: number; line: number | null } {
  let best: { delta: number; line: number } | null = null;
  const anchors = [0, size / 2, size];
  for (const a of anchors) {
    for (const line of lines) {
      const delta = line - (start + a);
      if (
        Math.abs(delta) <= threshold &&
        (!best || Math.abs(delta) < Math.abs(best.delta))
      ) {
        best = { delta, line };
      }
    }
  }
  return best
    ? { start: start + best.delta, line: best.line }
    : { start, line: null };
}

const roundGrid = (v: number) => Math.round(v / GRID_PX) * GRID_PX;

export function clampRect(rect: Rect, aspect: BroadcastAspect): Rect {
  const { w: sw, h: sh } = STAGE_SIZES[aspect];
  const w = clamp(Math.round(rect.w), 1, sw);
  const h = clamp(Math.round(rect.h), 1, sh);
  return {
    w,
    h,
    x: clamp(Math.round(rect.x), 0, sw - w),
    y: clamp(Math.round(rect.y), 0, sh - h),
  };
}

/** Move `origin` by (dx, dy) stage px with snapping; always inside the stage. */
export function moveRect(
  origin: Rect,
  dx: number,
  dy: number,
  aspect: BroadcastAspect,
  opts: { snap: boolean } = { snap: true },
): { rect: Rect; guides: Guides } {
  const lines = snapLines(aspect);
  let x = origin.x + dx;
  let y = origin.y + dy;
  const guides: Guides = { x: [], y: [] };
  if (opts.snap) {
    const sx = snapSpan(x, origin.w, lines.x);
    const sy = snapSpan(y, origin.h, lines.y);
    x = sx.line === null ? roundGrid(x) : sx.start;
    y = sy.line === null ? roundGrid(y) : sy.start;
    if (sx.line !== null) guides.x.push(sx.line);
    if (sy.line !== null) guides.y.push(sy.line);
  }
  return {
    rect: clampRect({ ...origin, x, y }, aspect),
    guides,
  };
}

/** Resize `origin` by dragging `handle` (dx, dy in stage px). */
export function resizeRect(
  origin: Rect,
  handle: ResizeHandle,
  dx: number,
  dy: number,
  aspect: BroadcastAspect,
  opts: { snap: boolean } = { snap: true },
): { rect: Rect; guides: Guides } {
  const { w: sw, h: sh } = STAGE_SIZES[aspect];
  const lines = snapLines(aspect);
  const guides: Guides = { x: [], y: [] };
  let left = origin.x;
  let top = origin.y;
  let right = origin.x + origin.w;
  let bottom = origin.y + origin.h;

  const snapEdge = (v: number, axis: "x" | "y") => {
    if (!opts.snap) return v;
    const arr = lines[axis];
    let best = v;
    let bestD = SNAP_THRESHOLD_PX + 1;
    for (const line of arr) {
      const d = Math.abs(line - v);
      if (d <= SNAP_THRESHOLD_PX && d < bestD) {
        best = line;
        bestD = d;
      }
    }
    if (bestD <= SNAP_THRESHOLD_PX) {
      guides[axis].push(best);
      return best;
    }
    return roundGrid(v);
  };

  if (handle.includes("w")) left = snapEdge(origin.x + dx, "x");
  if (handle.includes("e")) right = snapEdge(origin.x + origin.w + dx, "x");
  if (handle.includes("n")) top = snapEdge(origin.y + dy, "y");
  if (handle.includes("s")) bottom = snapEdge(origin.y + origin.h + dy, "y");

  left = clamp(left, 0, right - MIN_WIDGET_PX);
  right = clamp(right, left + MIN_WIDGET_PX, sw);
  top = clamp(top, 0, bottom - MIN_WIDGET_PX);
  bottom = clamp(bottom, top + MIN_WIDGET_PX, sh);

  return {
    rect: clampRect(
      { x: left, y: top, w: right - left, h: bottom - top },
      aspect,
    ),
    guides,
  };
}

/** Move one widget up/down in z-order; z values are renormalised (0..99). */
export function reorderZ(
  widgets: BroadcastWidget[],
  id: string,
  dir: "up" | "down",
): BroadcastWidget[] {
  const order = widgets
    .map((w, i) => ({ w, i }))
    .sort((a, b) => a.w.z - b.w.z || a.i - b.i);
  const pos = order.findIndex((o) => o.w.id === id);
  const target = dir === "up" ? pos + 1 : pos - 1;
  if (pos < 0 || target < 0 || target >= order.length) return widgets;
  [order[pos], order[target]] = [order[target], order[pos]];
  const step =
    order.length > 1 ? Math.min(4, Math.floor(98 / (order.length - 1))) : 0;
  const zById = new Map(order.map((o, rank) => [o.w.id, rank * step + 2]));
  return widgets.map((w) => ({ ...w, z: zById.get(w.id) ?? w.z }));
}

export const WIDGET_TYPES: readonly BroadcastWidgetType[] = [
  "scoreboard",
  "live_pitch",
  "callouts",
  "stats_panel",
  "heat_map",
  "area_control",
  "shot_map",
  "top_players",
  "image",
];

const DEFAULT_SIZE: Record<BroadcastWidgetType, { w: number; h: number }> = {
  scoreboard: { w: 800, h: 110 },
  live_pitch: { w: 960, h: 640 },
  callouts: { w: 1920, h: 1080 },
  stats_panel: { w: 560, h: 760 },
  heat_map: { w: 900, h: 460 },
  area_control: { w: 760, h: 520 },
  shot_map: { w: 900, h: 420 },
  top_players: { w: 760, h: 420 },
  image: { w: 480, h: 270 },
};

export const DEFAULT_OPTIONS: Record<
  BroadcastWidgetType,
  Record<string, unknown>
> = {
  scoreboard: { variant: "wide", show_logos: true, show_clock: true },
  live_pitch: {
    trail_length: 5,
    show_player_labels: false,
    use_team_colors: true,
  },
  callouts: {},
  stats_panel: { rows: [] },
  heat_map: { team: "both" },
  area_control: { team: "both" },
  shot_map: { team: "both" },
  top_players: { team: "both" },
  image: { asset_id: null, fit: "contain" },
};

export function nextWidgetId(
  widgets: BroadcastWidget[],
  type: BroadcastWidgetType,
): string {
  const ids = new Set(widgets.map((w) => w.id));
  let n = 1;
  while (ids.has(`${type.replace(/_/g, "-")}-${n}`)) n += 1;
  return `${type.replace(/_/g, "-")}-${n}`;
}

/** A new widget on top of the stack, centred, clamped inside the stage. */
export function createWidget(
  type: BroadcastWidgetType,
  widgets: BroadcastWidget[],
  aspect: BroadcastAspect,
): BroadcastWidget | null {
  if (widgets.length >= MAX_WIDGETS) return null;
  const { w: sw, h: sh } = STAGE_SIZES[aspect];
  const size = DEFAULT_SIZE[type];
  const w = Math.min(size.w, sw);
  const h = Math.min(size.h, sh);
  const top = widgets.reduce((m, x) => Math.max(m, x.z), 0);
  return {
    id: nextWidgetId(widgets, type),
    type,
    x: Math.round((sw - w) / 2),
    y: Math.round((sh - h) / 2),
    w,
    h,
    z: Math.min(99, top + 1),
    opacity: 1,
    text_scale: 1,
    visible: true,
    options: JSON.parse(JSON.stringify(DEFAULT_OPTIONS[type])),
  };
}
